/**
 * PricingZoneManager — Sprint M
 *
 * Sidebar CRUD for pricing zones. Each zone has a name, hex colour (via
 * react-colorful HexColorPicker), price in AUD dollars, description, and an
 * ordering value used across the buyer overview + legend.
 *
 * Delete is guarded: templates attached to a live event refuse zone deletes
 * with a 409 — surfaced as a toast in the parent.
 */
import React, { useState } from "react";
import { HexColorPicker } from "react-colorful";
import { Plus, Trash2, X, Check, Palette } from "lucide-react";
import { toast } from "sonner";
import { venuesApi } from "../../lib/events";
import { extractError } from "../../lib/api";

export default function PricingZoneManager({
  templateId,
  zones,
  disabled,
  onChange,
}) {
  const [creating, setCreating] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [draft, setDraft] = useState({
    name: "",
    hex_colour: "#ef4444",
    base_price_dollars: 0,
    description: "",
    display_order: 0,
  });

  const startCreate = () => {
    setEditingId(null);
    setCreating(true);
    setDraft({
      name: "",
      hex_colour: "#ef4444",
      base_price_dollars: 0,
      description: "",
      display_order: (zones?.length || 0) + 1,
    });
  };
  const startEdit = (z) => {
    setCreating(false);
    setEditingId(z.id);
    setDraft({
      name: z.name || "",
      hex_colour: z.hex_colour || "#ef4444",
      base_price_dollars: (z.base_price_cents || 0) / 100,
      description: z.description || "",
      display_order: z.display_order || 0,
    });
  };
  const cancel = () => {
    setCreating(false);
    setEditingId(null);
  };

  const save = async () => {
    const payload = {
      name: draft.name.trim(),
      hex_colour: draft.hex_colour,
      base_price_cents: Math.round(Number(draft.base_price_dollars) * 100),
      description: draft.description,
      display_order: Number(draft.display_order) || 0,
    };
    if (!payload.name) {
      toast.error("Give this zone a name.");
      return;
    }
    if (payload.base_price_cents < 0) {
      toast.error("Price can't be negative.");
      return;
    }
    try {
      if (creating) {
        const z = await venuesApi.createPricingZone(templateId, payload);
        toast.success(`Zone "${z.name}" added`);
      } else {
        await venuesApi.updatePricingZone(templateId, editingId, payload);
        toast.success("Zone updated");
      }
      cancel();
      onChange?.();
    } catch (err) {
      toast.error(extractError(err));
    }
  };

  const remove = async (z) => {
    if (!confirm(`Delete pricing zone "${z.name}"? Sections using it will be unassigned.`)) return;
    try {
      await venuesApi.deletePricingZone(templateId, z.id);
      toast.success("Zone removed");
      onChange?.();
    } catch (err) {
      toast.error(extractError(err));
    }
  };

  return (
    <div className="border border-[var(--gt-line)] rounded-sm bg-white" data-testid="pricing-zone-manager">
      <div className="flex items-center justify-between px-3 py-2 border-b border-[var(--gt-line)]">
        <div className="gt-label flex items-center gap-1.5">
          <Palette size={11} /> Pricing zones
        </div>
        <button
          type="button"
          onClick={startCreate}
          disabled={disabled}
          className="gt-btn-secondary text-[10px] px-2 py-1"
          data-testid="pricing-zone-add-btn"
        >
          <Plus size={11} /> Add
        </button>
      </div>

      <div className="p-3 space-y-2 max-h-[280px] overflow-y-auto">
        {(zones || []).length === 0 && !creating && (
          <p className="text-xs text-[var(--gt-ink-3)]">No zones yet — buyers won&apos;t see colours until you add at least one.</p>
        )}
        {(zones || []).map((z) => {
          const isEditing = editingId === z.id;
          if (isEditing) return null;
          return (
            <div
              key={z.id}
              className="flex items-center gap-2 p-2 rounded-sm border border-[var(--gt-line)] bg-[var(--gt-bg-surface)]"
              data-testid={`pricing-zone-item-${z.id}`}
            >
              <span
                className="inline-block w-5 h-5 rounded-sm border border-black/20 shrink-0"
                style={{ backgroundColor: z.hex_colour }}
              />
              <div className="flex-1 min-w-0">
                <div className="text-xs font-bold truncate">{z.name}</div>
                <div className="text-[10px] text-[var(--gt-ink-3)]">
                  A${((z.base_price_cents || 0) / 100).toFixed(2)} · used by {z.sections_assigned || 0} section{(z.sections_assigned || 0) === 1 ? "" : "s"}
                </div>
              </div>
              <button
                type="button"
                onClick={() => startEdit(z)}
                disabled={disabled}
                className="text-[10px] uppercase tracking-widest underline underline-offset-4 text-[var(--gt-ink-2)] hover:text-[var(--gt-ink)]"
                data-testid={`pricing-zone-edit-${z.id}`}
              >
                Edit
              </button>
              <button
                type="button"
                onClick={() => remove(z)}
                disabled={disabled}
                className="text-[var(--gt-ink-3)] hover:text-[var(--gt-accent)]"
                aria-label={`Delete ${z.name}`}
                data-testid={`pricing-zone-delete-${z.id}`}
              >
                <Trash2 size={13} />
              </button>
            </div>
          );
        })}
        {(creating || editingId) && (
          <div className="p-2 rounded-sm border border-[var(--gt-ink)] bg-white space-y-2" data-testid="pricing-zone-editor">
            <input
              type="text"
              placeholder="Zone name (e.g. VIP)"
              value={draft.name}
              onChange={(e) => setDraft((d) => ({ ...d, name: e.target.value }))}
              className="gt-input w-full text-xs"
              data-testid="pricing-zone-name-input"
            />
            <div className="flex gap-2">
              <div className="w-32">
                <HexColorPicker color={draft.hex_colour} onChange={(hex) => setDraft((d) => ({ ...d, hex_colour: hex }))} style={{ width: "100%", height: "100px" }} />
              </div>
              <div className="flex-1 space-y-1.5">
                <label className="text-[10px] uppercase tracking-widest text-[var(--gt-ink-3)] block">Hex</label>
                <input
                  type="text"
                  value={draft.hex_colour}
                  onChange={(e) => setDraft((d) => ({ ...d, hex_colour: e.target.value }))}
                  className="gt-input w-full text-xs font-mono"
                  data-testid="pricing-zone-hex-input"
                />
                <label className="text-[10px] uppercase tracking-widest text-[var(--gt-ink-3)] block">Price (AUD)</label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={draft.base_price_dollars}
                  onChange={(e) => setDraft((d) => ({ ...d, base_price_dollars: e.target.value }))}
                  className="gt-input w-full text-xs"
                  data-testid="pricing-zone-price-input"
                />
              </div>
            </div>
            <input
              type="text"
              placeholder="Description (optional)"
              value={draft.description}
              onChange={(e) => setDraft((d) => ({ ...d, description: e.target.value }))}
              className="gt-input w-full text-xs"
              data-testid="pricing-zone-desc-input"
            />
            <div className="flex justify-end gap-2 pt-1">
              <button type="button" onClick={cancel} className="gt-btn-ghost text-[10px]" data-testid="pricing-zone-cancel-btn">
                <X size={11} /> Cancel
              </button>
              <button type="button" onClick={save} className="gt-btn-primary text-[10px]" data-testid="pricing-zone-save-btn">
                <Check size={11} /> Save
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
